import type { LyricCue, SongSection } from '../types';
import { getTopHooks } from './hookDetection';

export interface SectionSpan {
  section: SongSection;
  startMs: number;
  endMs: number;
}

const SECTION_ORDER: SongSection[] = [
  'intro', 'verse', 'build', 'chorus', 'drop', 'verse', 'build', 'chorus', 'bridge', 'outro',
];

export function estimateSections(
  durationMs: number,
  lyrics: LyricCue[],
  bpm: number
): SectionSpan[] {
  const hooks = getTopHooks(lyrics, 4);
  const hookTimes = hooks.map((h) => h.startMs).sort((a, b) => a - b);

  if (durationMs <= 0) return [];

  const beatMs = 60_000 / Math.max(60, bpm);
  const introEnd = Math.min(durationMs * 0.12, hookTimes[0] ?? durationMs * 0.1);
  const outroStart = Math.max(durationMs * 0.88, hookTimes[hookTimes.length - 1] ?? durationMs * 0.9);

  const spans: SectionSpan[] = [
    { section: 'intro', startMs: 0, endMs: introEnd },
  ];

  let cursor = introEnd;
  let sectionIdx = 1;

  while (cursor < outroStart && sectionIdx < SECTION_ORDER.length) {
    const section = SECTION_ORDER[sectionIdx];
    let nearHook: number | undefined;
    for (const hookTime of hookTimes) {
      if (hookTime >= cursor && hookTime < cursor + durationMs * 0.18) {
        nearHook = hookTime;
        break;
      }
    }
    const baseLen = section === 'drop'
      ? beatMs * 16
      : section === 'build'
        ? beatMs * 32
        : section === 'chorus'
          ? beatMs * 64
          : beatMs * 48;

    let endMs = Math.min(outroStart, cursor + baseLen);
    if (nearHook !== undefined) {
      if (section === 'build') endMs = Math.min(outroStart, nearHook);
      if (section === 'chorus' || section === 'drop') {
        cursor = nearHook;
        endMs = Math.min(outroStart, nearHook + beatMs * 32);
      }
    }

    if (endMs - cursor < beatMs * 4) {
      sectionIdx += 1;
      continue;
    }

    spans.push({ section, startMs: cursor, endMs });
    cursor = endMs;
    sectionIdx += 1;
  }

  spans.push({ section: 'outro', startMs: outroStart, endMs: durationMs });
  return mergeAdjacentSections(spans);
}

function mergeAdjacentSections(spans: SectionSpan[]): SectionSpan[] {
  if (spans.length === 0) return spans;
  const merged: SectionSpan[] = [{ ...spans[0] }];
  for (let i = 1; i < spans.length; i++) {
    const prev = merged[merged.length - 1];
    const cur = spans[i];
    if (prev.section === cur.section) prev.endMs = cur.endMs;
    else merged.push({ ...cur });
  }
  return merged;
}

export function sectionAt(spans: SectionSpan[], progressMs: number): SongSection {
  const span = spans.find((s) => progressMs >= s.startMs && progressMs < s.endMs);
  return span?.section ?? 'verse';
}

export function sectionIntensity(section: SongSection): number {
  switch (section) {
    case 'intro': return 0.25;
    case 'verse': return 0.4;
    case 'build': return 0.65;
    case 'chorus': return 0.85;
    case 'drop': return 1;
    case 'bridge': return 0.5;
    case 'outro': return 0.3;
    default: return 0.5;
  }
}
